import React, { Component } from "react";
import PropTypes from "prop-types";

import LoadingOverlay from "./LoadingOverlay";
import { handleError } from "@/util/errorHandler";
import {
  renderTotal,
  renderPaginationPanel,
  renderNoData,
} from "@/util/paginationHelper";
import { debounce } from "lodash";
import ExportReportButton from "@/views/Components/ExportReport/ExportReportButton";
import { APP_CONST } from "@/store/dataStore";

class TableContainer extends Component {
  state = {
    page: 1,
    sizePerPage: 10,
    filter: "",
    sort: this.props.isTransaction ? "transactionCreatedTime,desc" : "",
    businessStatus: [],
    status: [],
    data: [],
    total: 0,
    isLoading: false,
    hasNext: false,
    hasPrevious: false,
  };

  createExportReportButton = () => {
    const params = {
      page: this.state.page - 1,
      size: this.state.sizePerPage,
      filter: this.state.filter,
      sort: this.state.sort,
      businessStatus: this.state.businessStatus,
      status: this.state.status,
      search: this.state.filter,
    };

    return (
      <ExportReportButton
        params={params}
        requestReport={this.props.requestReport}
        disableDateRangePicker={this.props.reportWithoutDateRange}
        initialDates={this.props.reportWithInitialDates}
        disableSelectedBeforeCurrentDate={
          this.props.isTransaction ? APP_CONST.REPORT_BACK_DAYS() : 0
        }
      />
    );
  };

  loadAndStoreData = async (
    page = 0,
    sizePerPage = 10,
    filter = "",
    sort = this.props.isTransaction ? "transactionCreatedTime,desc" : "",
    businessStatus = "",
    status = "",
  ) => {
    this.setState({ isLoading: true });

    try {
      const { data, total, hasNext, hasPrevious } = await this.props.loadData(
        page,
        sizePerPage,
        filter,
        sort,
        businessStatus,
        status,
      );
      this.setState({ data, total, hasNext, hasPrevious });
    } catch (error) {
      let errorMsg = error.response
        ? error.response.data.message
        : "Error Unknown";
      handleError(new Error(errorMsg));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleUpdateCurrentPage = () => {
    this.loadAndStoreData(
      this.state.page - 1,
      this.state.sizePerPage,
      this.state.filter,
      this.state.sort,
      this.state.businessStatus,
      this.state.status,
    );
  };

  handlePageChange = (page, sizePerPage = this.state.sizePerPage) => {
    this.setState({ page, sizePerPage });
    this.loadAndStoreData(
      page - 1,
      sizePerPage,
      this.state.filter,
      this.state.sort,
      this.state.businessStatus,
      this.state.status,
    );
  };

  handleSizePerPageChange = sizePerPage => {
    this.setState({ sizePerPage });
    this.loadAndStoreData(
      0,
      sizePerPage,
      this.state.filter,
      this.state.sort,
      this.state.businessStatus,
      this.state.status,
    );
  };

  handleFilterChange = filter => {
    this.setState({ filter, page: 1 });
    this.loadAndStoreData(
      0,
      this.state.sizePerPage,
      this.state.filter,
      this.state.sort,
      this.state.businessStatus,
      this.state.status,
    );
  };

  handleSortChange = (sortName, sortOrder) => {
    const sort = `${sortName},${sortOrder}`;

    this.setState({ sort });
    this.loadAndStoreData(
      0,
      this.state.sizePerPage,
      this.state.filter,
      sort,
      this.state.businessStatus,
      this.state.status,
    );
  };

  handleStatusChange = status => {
    this.setState({ status });
    this.loadAndStoreData(
      0,
      this.state.sizePerPage,
      this.state.filter,
      this.state.sort,
      this.state.businessStatus,
      status,
    );
  };

  handleBusinessStatusChange = businessStatus => {
    this.setState({ businessStatus });
    this.loadAndStoreData(
      0,
      this.state.sizePerPage,
      this.state.filter,
      this.state.sort,
      businessStatus,
      this.state.status,
    );
  };

  componentDidMount() {
    if (this.props.autoLoad) {
      this.loadAndStoreData();
    }
  }

  /*
   * This function is used to reload data if external parameters, passed to loadData, change
   * We need to switch to the first page but save everything, so we can use handlePageChange :)
   */
  handleExternalParametersChange = () => {
    this.handlePageChange(1, this.state.sizePerPage);
  };

  render() {
    const { page, sizePerPage } = this.state;

    const options = {
      sizePerPageList: [
        {
          text: "5",
          value: 5,
        },
        {
          text: "10",
          value: 10,
        },
        {
          text: "25",
          value: 25,
        },
        {
          text: "50",
          value: 50,
        },
      ],
      onPageChange: this.handlePageChange,
      onSizePerPageList: this.handleSizePerPageChange,
      onSearchChange:
        this.props.isTransaction === true
          ? filter => {
              this.setState({ filter, page: 1 });
            }
          : debounce(this.handleFilterChange, 1000),
      onSearchClicked: this.handleFilterChange,
      onSortChange: this.handleSortChange,
      page,
      sizePerPage,
      hidePageListOnlyOnePage: true,
      paginationShowsTotal: renderTotal,
      paginationPanel: renderPaginationPanel,
      noDataText: renderNoData(this.state.isLoading),
      exportCSVBtn: this.createExportReportButton,
    };

    const fetchInfo = {
      dataTotalSize: this.state.total,
      hasNext: this.state.hasNext,
      hasPrevious: this.state.hasPrevious,
    };

    // TODO: Maybe move this loader into the table, as it is done here:
    // https://stackoverflow.com/questions/43338884/how-add-react-loading-in-react-bootstrap-table
    return (
      <div style={{ position: "relative" }}>
        <LoadingOverlay loading={this.state.isLoading} />
        {React.cloneElement(this.props.children, {
          options: options,
          data: this.state.data,
          fetchInfo: fetchInfo,
          onBusinessStatusChange: this.handleBusinessStatusChange,
          onStatusChange: this.handleStatusChange,
        })}
      </div>
    );
  }
}

TableContainer.defaultProps = {
  autoLoad: true,
  reportWithoutDateRange: false,
  isTransaction: false,
};

TableContainer.propTypes = {
  loadData: PropTypes.func,
  requestReport: PropTypes.func.isRequired,
  children: PropTypes.element.isRequired,
  autoLoad: PropTypes.bool,
  reportWithoutDateRange: PropTypes.bool,
  reportWithInitialDates: ExportReportButton.propTypes.initialDates,
  isTransaction: PropTypes.bool,
};

export default TableContainer;
